import type { Color } from "../../common/color.js";
import { Control, type ControlOptions } from "../core/Control.js";
import type { MouseEventArgs, WheelEventArgs } from "../core/events.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { Theme } from "../themes/Theme.js";
import { Camera2D } from "../../engine2d/Camera2D.js";
import { Node2D } from "../../engine2d/Node2D.js";
import { Renderer2D } from "../../engine2d/Renderer2D.js";
import type { Vec2 } from "../../engine2d/math2d.js";

export interface Viewport2DOptions extends ControlOptions {
  scene?: Node2D;
  camera?: Camera2D;
  /** Behind the scene. Transparent by default, so the UI shows through. */
  clear?: Color;
  /** Sample the scene smoothly when it is drawn. Off for pixel art. */
  smooth?: boolean;
  /** Ctrl+wheel zooms about the pointer. */
  zoomOnWheel?: boolean;
  /** Drag with the middle button, or the left on empty space, to pan. */
  panOnDrag?: boolean;
}

/**
 * A window onto a 2D scene, as an ordinary Webwin control.
 *
 * This is the whole point of rendering to a texture. The scene is drawn in its
 * own pass with its own pipeline, and what reaches the screen is a bitmap —
 * so the viewport clips, scrolls, sits inside a window, obeys z-order and can
 * have UI laid over it, none of which would work if the engine were painting
 * straight onto the canvas.
 *
 * The scene is rendered during `onPaint`, which sounds wrong and is not:
 * `Desktop.render` *records* the control tree into a batch and only opens the
 * UI pass afterwards, so a control painting itself is still outside any pass
 * and free to submit one of its own.
 */
export class Viewport2D extends Control {
  scene: Node2D;
  camera: Camera2D;
  zoomOnWheel: boolean;
  panOnDrag: boolean;

  #renderer: Renderer2D | null = null;
  #clear: Color | undefined;
  #smooth: boolean;
  #panning = false;
  #panFrom: Vec2 = { x: 0, y: 0 };
  #cameraFrom: Vec2 = { x: 0, y: 0 };

  constructor(options: Viewport2DOptions = {}) {
    super({ width: 480, height: 320, tabStop: true, ...options });
    this.scene = options.scene ?? new Node2D({ name: "scene" });
    this.camera = options.camera ?? new Camera2D();
    this.#clear = options.clear;
    this.#smooth = options.smooth ?? true;
    this.zoomOnWheel = options.zoomOnWheel ?? true;
    this.panOnDrag = options.panOnDrag ?? true;

    this.on("pointerdown", (e) => this.#onPointerDown(e));
    this.on("pointermove", (e) => this.#onPointerMove(e));
    this.on("pointerup", () => this.#endPan());
    this.on("pointerleave", () => this.#endPan());
    this.on("wheel", (e) => this.#onWheel(e));
  }

  /** The renderer, once the control has found a GPU to build one with. */
  get renderer(): Renderer2D | null {
    return this.#renderer;
  }

  /** Sprites drawn and skipped on the last frame. */
  get stats(): { drawn: number; culled: number; draws: number } {
    return this.#renderer?.stats ?? { drawn: 0, culled: 0, draws: 0 };
  }

  /** A point in this control to a point in the world. */
  toWorld(local: Vec2): Vec2 {
    return this.camera.screenToWorld(local);
  }

  /** A world point to a point in this control. */
  fromWorld(world: Vec2): Vec2 {
    return this.camera.worldToScreen(world);
  }

  /** The whole scene is the drag, so a scrollable ancestor must not pan instead. */
  override handlesDragAt(): boolean {
    return this.panOnDrag;
  }

  #onPointerDown(e: MouseEventArgs): void {
    this.focus();
    if (!this.panOnDrag) return;
    this.#panning = true;
    this.#panFrom = { x: e.x, y: e.y };
    this.#cameraFrom = this.camera.position;
    this.desktop?.input.setCapture(this);
  }

  #onPointerMove(e: MouseEventArgs): void {
    if (!this.#panning) return;
    // Dragged in screen pixels, so the world has to move by the inverse of the
    // zoom — otherwise panning feels wrong at every zoom but 1.
    const zoom = this.camera.zoom || 1;
    this.camera.x = this.#cameraFrom.x - (e.x - this.#panFrom.x) / zoom;
    this.camera.y = this.#cameraFrom.y - (e.y - this.#panFrom.y) / zoom;
    this.invalidate();
  }

  #endPan(): void {
    if (!this.#panning) return;
    this.#panning = false;
    this.desktop?.input.releaseCapture(this);
  }

  #onWheel(e: WheelEventArgs): void {
    if (!this.zoomOnWheel || !e.ctrlKey || e.deltaY === 0) return;
    // Ctrl+wheel, matching what ScrollViewer already does, so the gesture
    // means the same thing wherever it is used.
    this.camera.zoomAt(e.deltaY < 0 ? 1.1 : 1 / 1.1, { x: e.x, y: e.y });
    e.handled = true;
    this.invalidate();
  }

  protected override onEnvironmentChanged(): void {
    // A viewport with no desktop has no GPU to build a renderer with; one that
    // is detached should not hold a texture open either.
    if (this.desktop) return;
    this.#renderer?.dispose();
    this.#renderer = null;
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const gpu = this.desktop?.gpu;
    const r = this.localRect;
    if (!gpu || r.width <= 0 || r.height <= 0) return;

    // Rendered at device resolution, so the scene is as sharp as the display
    // rather than being a screen-sized bitmap scaled up.
    const dpr = gpu.size.dpr;
    const width = Math.max(1, Math.round(r.width * rc.scale * dpr));
    const height = Math.max(1, Math.round(r.height * rc.scale * dpr));

    if (!this.#renderer) {
      this.#renderer = new Renderer2D(gpu, {
        width,
        height,
        smooth: this.#smooth,
        ...(this.#clear ? { clear: this.#clear } : {}),
      });
    } else {
      this.#renderer.resize(width, height);
    }

    this.#renderer.render(this.scene, this.camera);
    rc.drawTexture(this.#renderer.target, r);

    if (this.focused) theme.drawFocusRing(rc, r);
  }
}
